import React, { useEffect, useRef, useState } from "react";
import { TextareaAutosize, CircularProgress } from "@mui/material";
import { Send, ThumbUpOutlined, Check, InfoOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFetch } from "../../../fetch/fetch";
import styles from "./faqchat.module.css";

/**
 * 데스크 FAQ 질문하기 — 홈 탭 (스펙 7.1, 목업 ui/Main.html · NoMatch.html)
 *  - POST /api/faq/chat { question, sessionId, history } → { logId, noMatch, note, matches[], suggestedCategory, release }
 *  - POST /api/faq/feedback { logId, itemId?, type, comment? }
 *  - 세션: 진입 시 sessionId 발급, 최근 3턴 history 를 다음 요청에 동봉. "새 질문" 으로 초기화
 *  - 답변 본문은 서버가 준 FAQ 원문 그대로 (pre-wrap, URL 자동 링크). 모델 의역 없음
 *  - 카테고리로 찾기(browse) 탭은 사용자 결정으로 제외
 */

interface FaqMatch {
    id : number;
    role : "primary" | "related";
    category : string;
    subcategory : string;
    question : string;
    answer : string;
}

interface ChatResult {
    logId : number;
    noMatch : boolean;
    note : string;
    matches : FaqMatch[];
    suggestedCategory : string;
    release? : { version : number, itemCount : number, createdAt : string };
}

interface Turn {
    key : number;
    question : string;
    status : "loading" | "done" | "error";
    result? : ChatResult;
    errorText? : string;
    slow? : boolean;
}

type FeedbackType = "helpful" | "wrong_item" | "wrong_content" | "no_answer";

interface FeedbackState {
    sent? : FeedbackType;          // 이미 보낸 피드백 (버튼 비활성)
    openForm? : FeedbackType;      // 펼쳐진 코멘트 폼
    comment : string;
    sending? : boolean;
}

const toastOptions : any = { position : "bottom-right", autoClose : 3000, hideProgressBar : true, theme : "colored" };

const EXAMPLES = ["주말에도 결제 되나요?", "도시락 취소 언제까지 해요?", "지각 사유 증빙 뭐 받아요?", "환불 어떻게 해요?"];

const makeSessionId = () : string => {
    const c : any = (typeof window !== "undefined" && (window as any).crypto) ? (window as any).crypto : null;
    if (c && typeof c.randomUUID === "function") {
        return c.randomUUID();
    }
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const pad2 = (n : number) => (n < 10 ? "0" + n : "" + n);

const formatReleaseDate = (iso : string) : string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return "";
    }
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// 답변 원문 안의 URL 만 링크로 (라이브러리 없이)
const linkify = (text : string) : React.ReactNode[] => {
    const parts = String(text || "").split(/(https?:\/\/[^\s<>"']+)/g);
    return parts.map((p, i) => {
        if (/^https?:\/\//.test(p)) {
            return <a key={i} href={p} target="_blank" rel="noreferrer">{p}</a>;
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
    });
};

const FaqChat : React.FC<any> = (props : any) => {

    const [turns, setTurns] = useState<Turn[]>([]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState<string>(() => makeSessionId());
    const [feedback, setFeedback] = useState<{ [key : string] : FeedbackState }>({});
    const [expanded, setExpanded] = useState<{ [key : string] : boolean }>({});
    const [release, setRelease] = useState<{ version : number, itemCount : number, createdAt : string } | null>(null);

    const bodyRef = useRef<any>(null);
    const stickBottomRef = useRef(true);
    const sendingRef = useRef(false);
    const turnKeyRef = useRef(0);
    const textareaRef = useRef<any>(null);

    const isLoading = turns.some((t) => t.status === "loading");

    useEffect(() => {
        if (stickBottomRef.current && bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [turns, feedback, expanded]);

    const handleScroll = () => {
        const el = bodyRef.current;
        if (!el) {
            return;
        }
        stickBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
    };

    // 최근 3턴을 history 로 — 서버가 user/assistant 텍스트 턴으로 펼침
    const buildHistory = () => {
        return turns
            .filter((t) => t.status === "done" && t.result)
            .slice(-3)
            .map((t) => ({
                question : t.question,
                matchedIds : (t.result as ChatResult).matches.map((m) => ({ id : m.id, role : m.role })),
                note : (t.result as ChatResult).note || ""
            }));
    };

    const send = async (raw? : string) => {
        const question = String(raw !== undefined ? raw : input).trim();
        if (!question || sendingRef.current) {
            return;
        }
        sendingRef.current = true;
        stickBottomRef.current = true;
        const key = ++turnKeyRef.current;
        const history = buildHistory();
        setTurns((prev) => [...prev, { key, question, status : "loading" }]);
        setInput("");

        // 10초 넘으면 "조금만 더 기다려 주세요" (캐시 재작성 시 5초 이상 걸릴 수 있음)
        const slowTimer = setTimeout(() => {
            setTurns((prev) => prev.map((t) => (t.key === key && t.status === "loading" ? { ...t, slow : true } : t)));
        }, 10000);

        try {
            const result : any = await postFetch("/api/faq/chat", { question, sessionId, history });
            clearTimeout(slowTimer);
            if (result && result.message === "success") {
                if (result.release) {
                    setRelease(result.release);
                }
                setTurns((prev) => prev.map((t) => (t.key === key ? { ...t, status : "done", result } : t)));
            } else {
                const errorText = result && result.error ? result.error : (result && result.message === "NOT_AUTHORIZED" ? "권한이 없습니다" : "잠시 후 다시 시도해 주세요");
                setTurns((prev) => prev.map((t) => (t.key === key ? { ...t, status : "error", errorText } : t)));
            }
        } catch (e) {
            clearTimeout(slowTimer);
            console.log(e);
            setTurns((prev) => prev.map((t) => (t.key === key ? { ...t, status : "error", errorText : "잠시 후 다시 시도해 주세요" } : t)));
        } finally {
            sendingRef.current = false;
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        }
    };

    const retry = (turn : Turn) => {
        setTurns((prev) => prev.filter((t) => t.key !== turn.key));
        setTimeout(() => send(turn.question), 0);
    };

    const handleKeyDown = (e : any) => {
        if (e.nativeEvent && e.nativeEvent.isComposing) return;
        if (e.keyCode === 229) return;
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const resetSession = () => {
        setTurns([]);
        setFeedback({});
        setExpanded({});
        setSessionId(makeSessionId());
        setInput("");
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    // ── 피드백 ───────────────────────────────────────────────────────────
    const fbKey = (turnKey : number, itemId : number | null) => `${turnKey}-${itemId === null ? "none" : itemId}`;

    const getFb = (k : string) : FeedbackState => feedback[k] || { comment : "" };

    const patchFb = (k : string, patch : Partial<FeedbackState>) => {
        setFeedback((prev) => ({ ...prev, [k] : { ...(prev[k] || { comment : "" }), ...patch } }));
    };

    const submitFeedback = async (turn : Turn, itemId : number | null, type : FeedbackType, comment : string) => {
        if (!turn.result) {
            return;
        }
        const k = fbKey(turn.key, itemId);
        patchFb(k, { sending : true });
        try {
            const body : any = { logId : turn.result.logId, type };
            if (itemId !== null) {
                body.itemId = itemId;
            }
            if (comment && comment.trim()) {
                body.comment = comment.trim();
            }
            const result : any = await postFetch("/api/faq/feedback", body);
            if (result && result.message === "success") {
                patchFb(k, { sent : type, openForm : undefined, sending : false });
            } else {
                patchFb(k, { sending : false });
                toast.error("피드백 전송에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            patchFb(k, { sending : false });
            toast.error("피드백 전송에 실패했습니다", toastOptions);
        }
    };

    const renderFeedbackForm = (turn : Turn, itemId : number | null, k : string, fb : FeedbackState) => {
        if (!fb.openForm) {
            return null;
        }
        const label = fb.openForm === "wrong_content" ? "어떤 부분이 다른가요? (선택)"
            : fb.openForm === "wrong_item" ? "어떤 질문에 대한 답을 찾고 있었나요? (선택)"
            : "어떤 내용인지 조금 더 설명해 주세요 (선택)";
        return (
            <div className={styles.fbForm}>
                <div className={styles.fbLabel}>{label}</div>
                <textarea className={styles.fbTextarea} rows={2} value={fb.comment}
                    onChange={(e) => patchFb(k, { comment : e.target.value })}
                    placeholder="예: 실장님이 실물카드 말고 온라인도 된다고 하셨어요" />
                <div className={styles.fbActions}>
                    <button className={styles.btnGhost} onClick={() => patchFb(k, { openForm : undefined })}>취소</button>
                    <button className={styles.btnPrimary} disabled={!!fb.sending}
                        onClick={() => submitFeedback(turn, itemId, fb.openForm as FeedbackType, fb.comment)}>보내기</button>
                </div>
            </div>
        );
    };

    const renderCard = (turn : Turn, m : FaqMatch) => {
        const k = fbKey(turn.key, m.id);
        const fb = getFb(k);
        const isPrimary = m.role === "primary";
        const expKey = `${turn.key}-${m.id}`;
        const isCollapsed = !isPrimary && !expanded[expKey];
        return (
            <article key={m.id} className={`${styles.faqCard} ${isPrimary ? styles.primary : styles.related}`}>
                <div className={styles.cardMeta}>
                    <span className={`${styles.badge} ${isPrimary ? styles.badgePrimary : styles.badgeRelated}`}>{isPrimary ? "가장 적합" : "함께 보기"}</span>
                    <span>{m.category} &gt; {m.subcategory || "-"}</span>
                    <span>·</span>
                    <span>#{m.id}</span>
                </div>
                <h2 className={styles.cardQ}>{m.question}</h2>
                <p className={`${styles.cardA} ${isCollapsed ? styles.collapsed : ""}`}>{linkify(m.answer)}</p>
                <div className={styles.fbRow}>
                    {
                        fb.sent === "helpful" ?
                            <>
                                <button className={`${styles.fbBtn} ${styles.done}`} disabled><Check sx={{ fontSize : 16 }} /> 도움됐어요</button>
                                <span className={styles.thanks}>감사합니다</span>
                            </>
                            :
                            fb.sent ?
                                <span className={styles.thanks}>피드백 감사합니다. 실장이 확인합니다.</span>
                                :
                                <>
                                    <button className={styles.fbBtn} disabled={!!fb.sending} onClick={() => submitFeedback(turn, m.id, "helpful", "")}>
                                        <ThumbUpOutlined sx={{ fontSize : 16 }} /> 도움됐어요
                                    </button>
                                    <button className={`${styles.fbBtn} ${fb.openForm === "wrong_item" ? styles.pressed : ""}`}
                                        onClick={() => patchFb(k, { openForm : fb.openForm === "wrong_item" ? undefined : "wrong_item" })}>다른 항목이 나왔어요</button>
                                    <button className={`${styles.fbBtn} ${fb.openForm === "wrong_content" ? styles.pressed : ""}`}
                                        onClick={() => patchFb(k, { openForm : fb.openForm === "wrong_content" ? undefined : "wrong_content" })}>내용이 틀렸어요</button>
                                </>
                    }
                    {
                        !isPrimary &&
                        <button className={styles.moreBtn} onClick={() => setExpanded((prev) => ({ ...prev, [expKey] : !prev[expKey] }))}>
                            {isCollapsed ? "전체 보기" : "접기"}
                        </button>
                    }
                </div>
                {renderFeedbackForm(turn, m.id, k, fb)}
            </article>
        );
    };

    const renderNoAnswer = (turn : Turn, result : ChatResult) => {
        const k = fbKey(turn.key, null);
        const fb = getFb(k);
        if (result.noMatch) {
            return (
                <div className={styles.noMatchCard}>
                    <div className={styles.noMatchTitle}>
                        <WarningAmberOutlined sx={{ fontSize : 20, color : "#B45309" }} />
                        FAQ에서 맞는 항목을 못 찾았어요
                    </div>
                    <p className={styles.noMatchBody}>
                        이 질문은 실장 검토 목록에 자동으로 올라갔습니다. 급하면 데스크방에 물어봐 주세요.
                        {result.suggestedCategory ? <><br />추천 카테고리: <b>{result.suggestedCategory}</b></> : null}
                    </p>
                    <div className={styles.noMatchActions}>
                        {
                            fb.sent ?
                                <span className={styles.thanks}>설명이 전달됐습니다. 실장이 확인합니다.</span>
                                :
                                <button className={`${styles.fbBtn} ${fb.openForm === "no_answer" ? styles.pressed : ""}`}
                                    onClick={() => patchFb(k, { openForm : fb.openForm === "no_answer" ? undefined : "no_answer" })}>설명 덧붙이기</button>
                        }
                    </div>
                    {renderFeedbackForm(turn, null, k, fb)}
                </div>
            );
        }
        return (
            <div>
                <div className={styles.escapeRow}>
                    {
                        fb.sent ?
                            <span className={styles.thanks}>실장 검토 목록에 올라갔습니다.</span>
                            :
                            <>
                                <button className={`${styles.dashedBtn} ${fb.openForm === "no_answer" ? styles.pressed : ""}`}
                                    onClick={() => patchFb(k, { openForm : fb.openForm === "no_answer" ? undefined : "no_answer" })}>찾는 답이 없어요</button>
                                <span className={styles.hint}>누르면 실장 검토 목록에 올라갑니다</span>
                            </>
                    }
                </div>
                {fb.openForm ? <div style={{ marginTop : "10px" }}>{renderFeedbackForm(turn, null, k, fb)}</div> : null}
            </div>
        );
    };

    const renderTurn = (turn : Turn) => {
        return (
            <div key={turn.key} className={styles.turn}>
                <div className={styles.userRow}>
                    <div className={styles.userBubble}>{turn.question}</div>
                </div>
                {
                    turn.status === "loading" ?
                        <div className={styles.answerGroup}>
                            <div className={styles.skeletonCard}>
                                <div className={styles.skelBar} style={{ width : "30%" }} />
                                <div className={styles.skelBar} style={{ width : "70%" }} />
                                <div className={styles.skelBar} style={{ width : "95%" }} />
                                <div className={styles.skelBar} style={{ width : "85%" }} />
                            </div>
                            <div className={styles.loadingText}>
                                <CircularProgress size={14} sx={{ color : "#3E4DB4" }} />
                                {turn.slow ? "조금만 더 기다려 주세요…" : "FAQ에서 찾는 중…"}
                            </div>
                        </div>
                        :
                        turn.status === "error" ?
                            <div className={styles.errorCard}>
                                <span>{turn.errorText || "잠시 후 다시 시도해 주세요"}</span>
                                <button className={styles.btnGhost} onClick={() => retry(turn)}>다시 시도</button>
                            </div>
                            :
                            turn.result ?
                                <div className={styles.answerGroup}>
                                    {
                                        turn.result.note && !turn.result.noMatch ?
                                            <div className={styles.noteLine}>
                                                <InfoOutlined sx={{ fontSize : 16, color : "#6B7280" }} />
                                                {turn.result.note}
                                            </div>
                                            : null
                                    }
                                    {turn.result.matches.map((m) => renderCard(turn, m))}
                                    {renderNoAnswer(turn, turn.result)}
                                </div>
                                : null
                }
            </div>
        );
    };

    const now = new Date();
    const userName = props.user && props.user.name ? props.user.name : "";
    const userLocation = props.user && props.user.location ? props.user.location : "";

    return (
        <div className={styles.wrap}>
            <div className={styles.card}>
                <div className={styles.metaRow}>
                    <span>오늘 {pad2(now.getHours())}:{pad2(now.getMinutes())} · {userLocation ? `${userLocation} ` : ""}{userName}</span>
                    <button className={styles.newBtn} onClick={resetSession} disabled={turns.length === 0 && !input}>새 질문</button>
                </div>

                <div className={styles.body} ref={bodyRef} onScroll={handleScroll}>
                    {
                        turns.length === 0 ?
                            <div className={styles.empty}>
                                <div className={styles.emptyTitle}>데스크 FAQ에 물어보세요</div>
                                업무 중 궁금한 걸 그냥 말하듯 적으면 FAQ에서 맞는 항목을 찾아 원문을 보여줍니다.<br />
                                후속 질문("그럼 일요일은요?")도 이어서 할 수 있어요.
                                <div className={styles.exampleRow}>
                                    {EXAMPLES.map((ex) => <button key={ex} className={styles.exampleChip} onClick={() => send(ex)}>{ex}</button>)}
                                </div>
                            </div>
                            :
                            turns.map(renderTurn)
                    }
                </div>

                <div className={styles.inputBar}>
                    <TextareaAutosize
                        ref={textareaRef}
                        className={styles.textarea}
                        minRows={1}
                        maxRows={4}
                        value={input}
                        disabled={isLoading}
                        placeholder="질문을 입력하세요 (Enter 전송, Shift+Enter 줄바꿈)"
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button className={styles.sendBtn} aria-label="질문 보내기" disabled={isLoading || !input.trim()} onClick={() => send()}>
                        <Send sx={{ fontSize : 20 }} />
                    </button>
                </div>
            </div>
            {
                release ?
                    <div className={styles.footer}>FAQ v{release.version} · {release.itemCount}항목 · {formatReleaseDate(release.createdAt)} 배포</div>
                    : null
            }
        </div>
    );
};

export default FaqChat;
